import React from 'react';
import { get } from 'lodash/fp';
import DayDisplay from 'components/common/DateDisplay';
import PriceDisplayer from 'components/DisplayPrice';
import { SHIPPED_BUYER, SOLD, SHIPPED_STATES } from 'enums/order-state-enums';
import { sortStrings, sortNumbers, sortDates } from 'utils/table/sorters';
import {
  renderImageCell,
  renderPriceCell,
  renderStackedText,
  renderCTACell,
  renderTrackingLink,
  renderOrderStatus,
  renderStock,
} from 'utils/table/renderers';

// active listings Table column headers

export const activeColumns = [
  {
    dataIndex: 'gridPictureUrl',
    render: (image) => renderImageCell(image),
  },
  {
    title: 'NAME',
    dataIndex: '',
    sorter: (a, b) => sortStrings(a, b, 'brandName'),
    render: (product) => renderStackedText(product.brandName, product.name),
  },
  {
    title: 'SKU',
    dataIndex: 'sku',
    sorter: (a, b) => sortStrings(a, b, 'sku'),
    render: (sku) => renderStackedText(sku),
  },
  {
    title: 'COLOR',
    dataIndex: 'color',
    sorter: (a, b) => sortStrings(a, b, 'color'),
  },
  {
    title: 'STOCK',
    dataIndex: 'quantity',
    sorter: (a, b) => sortNumbers(a, b, 'quantity'),
    render: (quantity) => renderStock(quantity),
  },
  {
    title: 'PRICE',
    dataIndex: '',
    sorter: (a, b) => sortNumbers(a, b, 'priceCents'),
    render: (product) => {
      return <PriceDisplayer product={product} isStorePrice={product && !product.priceCents} />;
    },
  },
  {
    dataIndex: '',
    render: () => '',
  },
];

// Open Table column headers

export const openColumns = [
  {
    dataIndex: 'product.productTemplate.gridPictureUrl',
    render: (image) => renderImageCell(image),
  },
  {
    title: 'NAME',
    dataIndex: 'product.productTemplate',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.sku'),
    render: (product) => renderStackedText(product.sku, product.name),
  },
  {
    title: 'SIZE',
    dataIndex: 'product',
    render: (product, row) =>
      renderStackedText(
        get('sizeOption.presentation')(product) || null,
        `${get('product.productTemplate.sizeUnit')(row)} ${get(
          'product.productTemplate.singleGender',
        )(row)}` || null,
      ),
  },
  {
    title: 'COLOR',
    dataIndex: 'product.productTemplate.color',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.color'),
  },
  {
    title: 'DUE DATE',
    dataIndex: '',
    sorter: (a, b) => {
      try {
        const dayA = get('sellerShipByAt')(a);
        const dayB = get('sellerShipByAt')(b);
        return new Date(dayA) - new Date(dayB);
      } catch (e) {
        return 0;
      }
    },
    render: (order, row) => <DayDisplay date={get('sellerShipByAt')(order)} status={row.status} />,
  },
  {
    title: 'ACTION',
    render: (row) => {
      return SHIPPED_STATES[row.status] && SHIPPED_STATES[row.status].displayName === 'Shipped'
        ? renderTrackingLink(row)
        : renderCTACell(row);
    },
  },
  {
    dataIndex: '',
    render: () => '',
  },
];

// to confirm order columns

export const confirmColumns = [
  {
    dataIndex: 'product.productTemplate.gridPictureUrl',
    render: (image) => renderImageCell(image),
  },
  {
    title: 'NAME',
    dataIndex: 'product.productTemplate',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.sku'),
    render: (product) => renderStackedText(product.sku, product.name),
  },
  {
    title: 'SIZE',
    dataIndex: 'product',
    render: (product, row) =>
      renderStackedText(
        get('sizeOption.presentation')(product) || null,
        `${get('product.productTemplate.sizeUnit')(row)} ${get(
          'product.productTemplate.singleGender',
        )(row)}` || null,
      ),
  },
  {
    title: 'COLOR',
    dataIndex: 'product.productTemplate.color',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.color'),
  },
  {
    title: 'DUE DATE',
    dataIndex: 'purchasedAt',
    sorter: (a, b) => sortDates(a, b, 'purchasedAt'),
    render: (date) => <DayDisplay date={date} status={SOLD} />,
  },
  {
    title: 'ACTION',
    render: (row) => {
      return row.status === 'shipped_buyer' ? renderTrackingLink(row) : renderCTACell(row);
    },
  },
  {
    dataIndex: '',
    render: () => '',
  },
];

//  to ship orders columns

export const toShipColumns = [
  {
    dataIndex: 'product.productTemplate.gridPictureUrl',
    render: (image) => renderImageCell(image),
  },
  {
    title: 'NAME',
    dataIndex: 'product.productTemplate',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.sku'),
    render: (product) => renderStackedText(product.sku, product.name),
  },
  {
    title: 'SIZE',
    dataIndex: 'product',
    render: (product, row) =>
      renderStackedText(
        get('sizeOption.presentation')(product) || null,
        `${get('product.productTemplate.sizeUnit')(row)} ${get(
          'product.productTemplate.singleGender',
        )(row)}` || null,
      ),
  },
  {
    title: 'COLOR',
    dataIndex: 'product.productTemplate.color',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.color'),
  },
  {
    title: 'DUE DATE',
    dataIndex: 'sellerShipByAt',
    sorter: (a, b) => sortDates(a, b, 'sellerShipByAt'),
    render: (date) => <DayDisplay date={date} />,
  },
  {
    title: 'ACTION',
    render: (row) =>
      row.status === 'shipped_buyer' ? renderTrackingLink(row) : renderCTACell(row),
  },
  {
    dataIndex: '',
    render: () => '',
  },
];

// shipped orders columns

export const shippedColumns = [
  {
    dataIndex: 'product.productTemplate.gridPictureUrl',
    render: (image) => renderImageCell(image),
  },
  {
    title: 'NAME',
    dataIndex: 'product.productTemplate',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.sku'),
    render: (product) => renderStackedText(product.sku, product.name),
  },
  {
    title: 'SIZE',
    dataIndex: 'product',
    render: (product, row) =>
      renderStackedText(
        get('sizeOption.presentation')(product) || null,
        `${get('product.productTemplate.sizeUnit')(row)} ${get(
          'product.productTemplate.singleGender',
        )(row)}` || null,
      ),
  },
  {
    title: 'COLOR',
    dataIndex: 'product.productTemplate.color',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.color'),
  },
  {
    title: 'SHIPPED',
    dataIndex: 'shippedAt',
    sorter: (a, b) => sortDates(a, b, 'shippedAt'),
    render: (date) => <DayDisplay date={date} status={SHIPPED_BUYER} completed />,
  },
  {
    title: 'ACTION',
    dataIndex: 'trackingCode',
    sorter: (a, b) => sortStrings(a, b, 'trackingCode'),
    render: (trackingCode, row) => renderTrackingLink(row),
  },
  {
    dataIndex: '',
    render: () => '',
  },
];

export const completedColumns = [
  {
    dataIndex: 'product.productTemplate.gridPictureUrl',
    render: (image) => renderImageCell(image),
  },
  {
    title: 'NAME',
    dataIndex: 'product.productTemplate',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.sku'),
    render: (product) => renderStackedText(product.sku, product.name),
  },
  {
    title: 'SIZE',
    dataIndex: 'product',
    render: (product, row) =>
      renderStackedText(
        get('sizeOption.presentation')(product) || null,
        `${get('product.productTemplate.sizeUnit')(row)} ${get(
          'product.productTemplate.singleGender',
        )(row)}` || null,
      ),
  },
  {
    title: 'COLOR',
    dataIndex: 'product.productTemplate.color',
    sorter: (a, b) => sortStrings(a, b, 'product.productTemplate.color'),
  },
  {
    title: 'COMPLETED',
    dataIndex: 'updatedAt',
    sorter: (a, b) => sortDates(a, b, 'updatedAt'),
    render: (date) => <DayDisplay date={date} completed />,
  },
  {
    title: 'STATUS',
    render: (order) => renderOrderStatus(order),
  },
  {
    dataIndex: '',
    render: () => '',
  },
];

// table loading columns
export const activeColumnsLoading = [
  {
    dataIndex: 'name',
    render: () => <div className="shoe-box" />,
  },
  {
    title: 'NAME',
    dataIndex: 'name',
    key: 'name1',
    render: () => <div className="loader-box" />,
  },
  {
    title: 'COLOR',
    dataIndex: 'name',
    key: 'name2',
    render: () => <div className="loader-box" />,
  },
  {
    title: 'DATE',
    dataIndex: 'name',
    key: 'name3',
    render: () => <div className="loader-box" />,
  },
  {
    title: 'PRICE',
    dataIndex: 'name',
    key: 'name4',
    render: () => <div className="loader-box" />,
  },
];

// sub table loading columns
export const subColumnsLoading = [
  {
    title: '',
    dataIndex: '',
  },
  {
    title: '',
    dataIndex: '',
  },
  {
    title: 'SIZES',
    dataIndex: 'name',
    key: 'name1',
    render: () => <div className="sub-loader-box" />,
  },
  {
    title: 'SKU',
    dataIndex: 'name',
    key: 'name2',
    render: () => <div className="sub-loader-box" />,
  },
  {
    title: 'STOCK',
    dataIndex: 'name',
    key: 'name3',
    render: () => <div className="sub-loader-box" />,
  },
  {
    title: 'LAST SALE',
    dataIndex: 'name',
    key: 'name4',
    render: () => <div className="sub-loader-box" />,
  },
  {
    title: 'LOWEST',
    dataIndex: 'name',
    key: 'name5',
    render: () => <div className="sub-loader-box" />,
  },
];

export const activeListingsSubColumns = [
  {
    title: '',
    dataIndex: '',
  },
  {
    title: '',
    dataIndex: '',
  },
  {
    title: 'SIZES',
    dataIndex: '',
    render: (product, row) =>
      renderStackedText(
        get('sizeOption.presentation')(product) || null,
        `${get('sizeUnit')(row)} ${get('singleGender')(row)}` || null,
      ),
  },
  {
    title: 'STOCK',
    dataIndex: 'quantity',
    sorter: (a, b) => sortNumbers(a, b, 'quantity'),
    render: (quantity) => renderStock(quantity),
  },
  {
    title: 'LAST SALE',
    dataIndex: 'lastSoldPriceCents',
    sorter: (a, b) => sortNumbers(a, b, 'lastSoldPriceCents'),
    render: (price) => renderPriceCell(price),
  },
  {
    title: 'LOWEST',
    dataIndex: 'lowestPriceCents',
    sorter: (a, b) => sortNumbers(a, b, 'lowestPriceCents'),
    render: (price) => renderPriceCell(price),
  },
];
